import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Upload } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { Textarea } from 'components/ui/textarea';
import { useToast } from 'hooks/use-toast';
import { useImportOpenapi } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import { NotificationType } from 'features/chat/types/notification-types';
import { useNotificationListener } from 'hooks/use-notification-listener';
import { useTranslation } from 'react-i18next';

type ImportSwaggerModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  toolId: string;
  projectKey: string;
};

type Phase = 'input' | 'importing' | 'done';
type SpecMode = 'url' | 'paste';

interface IngestResult {
  success?: boolean;
  status?: string;
  found?: number;
  converted?: number;
  skipped?: number | Array<{ op?: string; reason?: string }>;
  error?: string;
}

export function ImportSwaggerModal({ open, onOpenChange, toolId, projectKey }: ImportSwaggerModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync: importOpenapi, isPending } = useImportOpenapi();
  // Completion is driven by the real-time SignalR DevNotification (same channel as RAG uploads).

  const [phase, setPhase] = useState<Phase>('input');
  const [mode, setMode] = useState<SpecMode>('url');
  const [specUrl, setSpecUrl] = useState('');
  const [specContent, setSpecContent] = useState('');
  const [ingestResult, setIngestResult] = useState<IngestResult | null>(null);

  const isFailed = ingestResult ? ingestResult.success === false || ingestResult.status === 'failed' : false;

  useEffect(() => {
    if (!open) {
      setPhase('input');
      setMode('url');
      setSpecUrl('');
      setSpecContent('');
      setIngestResult(null);
    }
  }, [open]);

  useNotificationListener('blocks-notification', (event) => {
    const raw = (event as { message?: { denormalizedPayload?: unknown } })?.message?.denormalizedPayload;
    if (typeof raw !== 'string') return;

    try {
      const payload = JSON.parse(raw);
      if (payload?.type !== NotificationType.MCP_INGEST || payload?.file_id !== toolId) return;

      setIngestResult({
        success: payload.success,
        status: payload.status,
        found: payload.found,
        converted: payload.converted,
        skipped: payload.skipped,
        error: payload.error,
      });
      setPhase('done');
      // Refresh the tool detail so the API tab shows the imported operations.
      void queryClient.invalidateQueries({ queryKey: ['tools', 'detail', toolId, projectKey] });
    } catch {
      /* ignore unrelated / malformed notifications */
    }
  });

  const handleFile = async (file?: File | null) => {
    if (!file) return;
    const text = await file.text();
    setSpecContent(text);
    setMode('paste');
  };

  const canSubmit = mode === 'url' ? specUrl.trim().length > 0 : specContent.trim().length > 0;

  const handleImport = async () => {
    try {
      await importOpenapi({
        toolId,
        payload: mode === 'url' ? { specUrl: specUrl.trim() } : { specContent },
      });
      setIngestResult(null);
      setPhase('importing');
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('IMPORT_FAILED') || 'Import failed',
        description: error instanceof Error ? error.message : t('SOMETHING_WENT_WRONG') || 'Something went wrong.',
      });
    }
  };

  const skippedList = Array.isArray(ingestResult?.skipped) ? ingestResult!.skipped : [];
  const skippedCount = Array.isArray(ingestResult?.skipped)
    ? ingestResult!.skipped.length
    : typeof ingestResult?.skipped === 'number'
      ? ingestResult.skipped
      : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{t('IMPORT_FROM_SWAGGER') || 'Import from Swagger'}</DialogTitle>
          <DialogDescription>
            {t('IMPORT_FROM_SWAGGER_DESCRIPTION') ||
              'Provide an OpenAPI/Swagger spec (2.0, 3.0 or 3.1). Operations are imported as editable API actions.'}
          </DialogDescription>
        </DialogHeader>

        {phase === 'input' && (
          <div className="flex flex-col gap-4 overflow-y-auto">
            <RadioGroup
              value={mode}
              onValueChange={(value) => setMode(value as SpecMode)}
              className="flex gap-6"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="url" id="spec-url-mode" />
                <Label htmlFor="spec-url-mode" className="font-normal">
                  {t('SPEC_URL') || 'Spec URL'}
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="paste" id="spec-paste-mode" />
                <Label htmlFor="spec-paste-mode" className="font-normal">
                  {t('PASTE_OR_UPLOAD') || 'Paste / Upload'}
                </Label>
              </div>
            </RadioGroup>

            {mode === 'url' ? (
              <div className="flex flex-col gap-2">
                <Label htmlFor="spec-url">{t('SPEC_URL') || 'Spec URL'}</Label>
                <Input
                  id="spec-url"
                  value={specUrl}
                  onChange={(event) => setSpecUrl(event.target.value)}
                  placeholder="https://api.example.com/openapi.json"
                />
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="spec-content">{t('SPEC_CONTENT') || 'Spec content'}</Label>
                  <label className="inline-flex cursor-pointer items-center gap-1.5 text-sm">
                    <Upload className="h-4 w-4" />
                    {t('UPLOAD_FILE') || 'Upload file'}
                    <input
                      type="file"
                      accept=".json,.yaml,.yml,application/json,text/yaml"
                      className="hidden"
                      onChange={(event) => handleFile(event.target.files?.[0])}
                    />
                  </label>
                </div>
                <Textarea
                  id="spec-content"
                  value={specContent}
                  onChange={(event) => setSpecContent(event.target.value)}
                  className="min-h-[280px] resize-none font-mono text-sm"
                  spellCheck={false}
                  placeholder='{ "openapi": "3.0.0", ... }'
                />
              </div>
            )}
          </div>
        )}

        {phase === 'importing' && (
          <div className="flex h-[280px] flex-col items-center justify-center gap-3 text-center">
            <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
            <p className="text-sm font-medium">{t('IMPORTING_SPEC') || 'Importing spec...'}</p>
            <p className="max-w-md text-sm text-muted-foreground">
              {t('IMPORTING_SPEC_DESCRIPTION') ||
                'Parsing operations and proposing authentication. This may take a moment.'}
            </p>
          </div>
        )}

        {phase === 'done' && isFailed && (
          <div className="flex flex-col gap-3 overflow-y-auto">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <p className="text-sm font-medium">{t('IMPORT_FAILED') || 'Import failed'}</p>
            </div>
            <p className="text-sm text-muted-foreground">
              {ingestResult?.error || t('IMPORT_FAILED_DESCRIPTION') || 'The spec could not be imported.'}
            </p>
          </div>
        )}

        {phase === 'done' && !isFailed && (
          <div className="flex flex-col gap-4 overflow-y-auto">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
              <p className="text-sm font-medium">{t('IMPORT_COMPLETE') || 'Import complete'}</p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border border-border p-3 text-center">
                <p className="text-lg font-semibold">{ingestResult?.found ?? 0}</p>
                <p className="text-xs text-muted-foreground">{t('FOUND') || 'Found'}</p>
              </div>
              <div className="rounded-lg border border-border p-3 text-center">
                <p className="text-lg font-semibold">{ingestResult?.converted ?? 0}</p>
                <p className="text-xs text-muted-foreground">{t('CONVERTED') || 'Converted'}</p>
              </div>
              <div className="rounded-lg border border-border p-3 text-center">
                <p className="text-lg font-semibold">{skippedCount}</p>
                <p className="text-xs text-muted-foreground">{t('SKIPPED') || 'Skipped'}</p>
              </div>
            </div>
            {skippedList.length > 0 && (
              <div className="max-h-40 overflow-auto rounded-lg border border-border p-3">
                <p className="mb-2 text-xs font-medium text-muted-foreground">{t('SKIPPED') || 'Skipped'}</p>
                <ul className="space-y-1 text-xs text-muted-foreground">
                  {skippedList.map((item, index) => (
                    <li key={`${item.op ?? 'op'}-${index}`}>
                      <span className="font-medium text-foreground">{item.op || '—'}</span>
                      {item.reason ? ` — ${item.reason}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {phase === 'input' ? (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
                {t('CANCEL') || 'Cancel'}
              </Button>
              <Button onClick={handleImport} disabled={!canSubmit || isPending}>
                {isPending ? t('IMPORTING') || 'Importing...' : t('IMPORT') || 'Import'}
              </Button>
            </>
          ) : (
            <Button onClick={() => onOpenChange(false)} disabled={phase === 'importing'}>
              {phase === 'importing' ? t('IMPORTING') || 'Importing...' : t('DONE') || 'Done'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
